import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Mail, MapPin, Clock, MessageCircle, Send, CheckCircle2 } from "lucide-react";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";
import { toast } from "sonner";
import site from "@/data/site.json";

const schema = z.object({
    name: z.string().min(2, "Informe seu nome (mínimo 2 letras)"),
    email: z.string().email("Informe um e-mail válido"),
    phone: z.string().min(8, "Informe um telefone válido com DDD"),
    petName: z.string().max(60).optional().or(z.literal("")),
    message: z.string().min(10, "Conte um pouco mais — mínimo de 10 caracteres"),
});

export default function Contact() {
    const [sent, setSent] = useState(false);
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm({ resolver: zodResolver(schema), mode: "onBlur" });

    const onSubmit = async () => {
        await new Promise((r) => setTimeout(r, 1400));
        setSent(true);
        reset();
        toast.success("Mensagem demo enviada com sucesso", {
            description: "Formulário demonstrativo — nada foi coletado ou armazenado.",
        });
    };

    return (
        <section id="contato" data-testid="contact-section" className="py-20 lg:py-28">
            <div className="container-x">
                <SectionHeading
                    eyebrow="Contato"
                    title={
                        <>
                            Vamos mimar <em className="text-coral">seu melhor amigo</em>?
                        </>
                    }
                    description="Formulário demonstrativo com validação completa — nenhuma mensagem é enviada ou armazenada nesta versão."
                />

                <div className="mt-12 grid overflow-hidden rounded-[2.5rem] border border-sand shadow-[0_24px_60px_rgba(23,61,53,0.1)] lg:grid-cols-[0.85fr_1.15fr]">
                    <div className="grain relative bg-forest p-8 text-cream sm:p-10">
                        <h3 className="font-serif text-2xl font-bold">Fale com a gente</h3>
                        <p className="mt-2 text-sm text-cream/70">
                            Dúvidas, agendamentos e sugestões — responderemos rapidinho (em uma loja real, é claro).
                        </p>
                        <ul className="mt-8 flex flex-col gap-5">
                            <li className="flex items-start gap-3.5">
                                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-coral/20 text-coral">
                                    <MessageCircle className="h-5 w-5" />
                                </span>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-cream/50">WhatsApp</p>
                                    <p className="text-sm font-bold">{site.contact.whatsappLabel} (demo)</p>
                                </div>
                            </li>
                            <li className="flex items-start gap-3.5">
                                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-coral/20 text-coral">
                                    <Mail className="h-5 w-5" />
                                </span>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-cream/50">E-mail</p>
                                    <p className="text-sm font-bold">{site.contact.emailLabel}</p>
                                </div>
                            </li>
                            <li className="flex items-start gap-3.5">
                                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-coral/20 text-coral">
                                    <MapPin className="h-5 w-5" />
                                </span>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-cream/50">Endereço</p>
                                    <p className="text-sm font-bold">{site.contact.addressLabel}</p>
                                    <p className="text-xs text-cream/60">{site.contact.cityLabel}</p>
                                </div>
                            </li>
                            <li className="flex items-start gap-3.5">
                                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-coral/20 text-coral">
                                    <Clock className="h-5 w-5" />
                                </span>
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-cream/50">Horários</p>
                                    {site.contact.hours.map((h) => (
                                        <p key={h.days} className="text-sm font-bold">
                                            {h.days} <span className="font-medium text-cream/60">· {h.time}</span>
                                        </p>
                                    ))}
                                </div>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-cream-card p-8 sm:p-10">
                        {sent ? (
                            <div
                                className="flex h-full flex-col items-center justify-center gap-4 py-10 text-center"
                                data-testid="contact-success"
                            >
                                <span className="grid h-16 w-16 place-items-center rounded-full bg-coral/20">
                                    <CheckCircle2 className="h-8 w-8 text-coral-dark" />
                                </span>
                                <p className="font-serif text-2xl font-bold text-forest">Mensagem demo enviada!</p>
                                <p className="max-w-sm text-sm text-graphite-muted">
                                    Obrigada pelo teste do formulário. Nada foi coletado — em uma loja real, você
                                    receberia resposta por e-mail ou WhatsApp.
                                </p>
                                <button
                                    type="button"
                                    data-testid="contact-success-reset"
                                    onClick={() => setSent(false)}
                                    className="mt-2 rounded-full border-2 border-forest/15 px-6 py-3 text-sm font-bold text-forest transition-all hover:border-coral hover:text-coral-dark"
                                >
                                    Enviar outra (demo)
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <Field label="Nome do tutor *" error={errors.name?.message}>
                                        <input
                                            {...register("name")}
                                            type="text"
                                            placeholder="Seu nome"
                                            data-testid="contact-name-input"
                                            className={inputCls(errors.name)}
                                        />
                                    </Field>
                                    <Field label="Nome do pet" error={errors.petName?.message}>
                                        <input
                                            {...register("petName")}
                                            type="text"
                                            placeholder="Ex.: Mimo (opcional)"
                                            data-testid="contact-pet-input"
                                            className={inputCls(errors.petName)}
                                        />
                                    </Field>
                                </div>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <Field label="E-mail *" error={errors.email?.message}>
                                        <input
                                            {...register("email")}
                                            type="email"
                                            placeholder="voce@email.com"
                                            data-testid="contact-email-input"
                                            className={inputCls(errors.email)}
                                        />
                                    </Field>
                                    <Field label="Telefone *" error={errors.phone?.message}>
                                        <input
                                            {...register("phone")}
                                            type="tel"
                                            placeholder="(11) 99999-9999"
                                            data-testid="contact-phone-input"
                                            className={inputCls(errors.phone)}
                                        />
                                    </Field>
                                </div>
                                <Field label="Mensagem *" error={errors.message?.message}>
                                    <textarea
                                        {...register("message")}
                                        rows={4}
                                        placeholder="Conte o que seu pet precisa..."
                                        data-testid="contact-message-input"
                                        className={`${inputCls(errors.message)} resize-none`}
                                    />
                                </Field>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    data-testid="contact-form-submit-button"
                                    className="mt-1 inline-flex items-center justify-center gap-2.5 rounded-full bg-forest py-4 text-sm font-bold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-light hover:shadow-[0_16px_32px_rgba(23,61,53,0.25)] disabled:opacity-70"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Enviando demo...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="h-4 w-4" />
                                            Enviar mensagem (demo)
                                        </>
                                    )}
                                </button>
                                <p className="text-center text-[11px] text-graphite-muted">
                                    Demonstração de interface — nenhum dado sai do seu navegador.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

const inputCls = (error) =>
    `rounded-xl border bg-cream/70 px-3.5 py-2.5 text-sm font-medium text-forest placeholder:text-forest/30 focus:outline-none focus:ring-2 ${
        error ? "border-destructive focus:ring-destructive/25" : "border-sand focus:border-coral focus:ring-coral/30"
    }`;

function Field({ label, error, children }) {
    return (
        <label className="flex flex-col gap-1.5 text-xs font-bold text-forest">
            {label}
            {children}
            {error && (
                <span role="alert" className="text-[11px] font-semibold text-destructive" data-testid="field-error">
                    {error}
                </span>
            )}
        </label>
    );
}
